import { useState } from 'react';
import { SUBJECT_COLORS, SUBJECT_EMOJIS } from '../data';

const EXAM_SUBJECTS = ['Math', 'Science', 'Social Studies', 'Language Arts'];

function ScoreBar({ pct, color }) {
  return (
    <div className="progress-bar-bg" style={{ height: 14, marginTop: 6 }}>
      <div
        className="progress-bar-fill"
        style={{ width: `${pct}%`, background: color }}
      />
    </div>
  );
}

function verdict(pct) {
  if (pct >= 75) return { label: 'GED Ready!', icon: '🎉', cls: 'verdict--pass' };
  if (pct >= 60) return { label: 'Almost Ready', icon: '🟡', cls: 'verdict--close' };
  return { label: 'Keep Studying', icon: '📚', cls: 'verdict--fail' };
}

// GED passing score: 150/200 = 75%
const PASS_PCT = 75;

export default function ExamResults({ results, onStudy }) {
  const { scores, essay, isPremium } = results;
  const [copied, setCopied] = useState(false);

  // Overall MC score
  const totalCorrect = EXAM_SUBJECTS.reduce((s, sub) => s + (scores[sub]?.correct || 0), 0);
  const totalQ = EXAM_SUBJECTS.reduce((s, sub) => s + (scores[sub]?.total || 0), 0);
  const mcPct = totalQ > 0 ? Math.round((totalCorrect / totalQ) * 100) : 0;

  // Overall including essay
  const essayPct = essay ? Math.round((essay.total / 6) * 100) : null;
  const overallPct = essayPct !== null
    ? Math.round(mcPct * 0.8 + essayPct * 0.2)
    : mcPct;

  const v = verdict(overallPct);

  function handleShare() {
    const lines = [
      `📊 My GED Ready Prep Score: ${overallPct}% — ${v.label}`,
      '',
      ...EXAM_SUBJECTS.filter(s => scores[s]?.total > 0).map(s => {
        const pct = Math.round((scores[s].correct / scores[s].total) * 100);
        return `${SUBJECT_EMOJIS[s]} ${s}: ${pct}%`;
      }),
      essay ? `✍️ Essay: ${essay.total}/6` : '',
      '',
      '🎓 Practice at gedreadyprep.com',
    ].filter(l => l !== undefined);
    navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  return (
    <div className="exam-results">
      {/* Overall verdict */}
      <div className="results-hero">
        <div className="results-hero-icon">{v.icon}</div>
        <h1 className="results-hero-score">{overallPct}%</h1>
        <p className={`results-verdict ${v.cls}`}>{v.label}</p>
        <p className="results-hero-sub">{totalCorrect} of {totalQ} multiple choice correct{essay ? ` · Essay ${essay.total}/6` : ''}</p>
      </div>

      {/* Per-subject score cards */}
      <div className="results-section">
        <h2 className="results-section-title">Subject Scores <span className="results-scale-note">Pass = 150/200 (75%)</span></h2>
        <div className="results-subject-grid">
          {EXAM_SUBJECTS.filter(s => scores[s]?.total > 0).map(s => {
            const sc = scores[s];
            const pct = Math.round((sc.correct / sc.total) * 100);
            const pass = pct >= PASS_PCT;
            return (
              <div key={s} className={`results-subject-card ${pass ? 'results-subject-card--pass' : 'results-subject-card--fail'}`}>
                <div className="results-subject-top">
                  <span className="results-subject-emoji">{SUBJECT_EMOJIS[s]}</span>
                  <div>
                    <p className="results-subject-name">{s}</p>
                    <p className="results-subject-score">{sc.correct}/{sc.total} correct</p>
                  </div>
                </div>
                <div className="results-subject-pct-row">
                  <span className="results-subject-pct">{pct}%</span>
                  <span className={`results-pass-badge ${pass ? 'results-pass-badge--pass' : 'results-pass-badge--fail'}`}>
                    {pass ? '✓ PASS' : '✗ FAIL'}
                  </span>
                </div>
                <ScoreBar pct={pct} color={SUBJECT_COLORS[s]} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Essay breakdown */}
      {essay && (
        <div className="results-section">
          <h2 className="results-section-title">Essay Score — {essay.total}/6</h2>
          <div className="essay-grade-summary">
            <p className="essay-grade-summary-text">{essay.summary}</p>
          </div>
          <div className="essay-traits">
            {[essay.trait1, essay.trait2, essay.trait3].map(t => (
              <div key={t.name} className="essay-trait-card">
                <div className="essay-trait-header">
                  <span className="essay-trait-name">{t.name}</span>
                  <span className={`essay-trait-score essay-trait-score--${t.score}`}>{t.score}/2</span>
                </div>
                <p className="essay-trait-feedback">{t.feedback}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isPremium && (
        <div className="results-free-note">
          This was a 2-question preview. Upgrade for the full 40-question mock exam + essay grading.
        </div>
      )}

      <div className="results-actions">
        <button className="primary-btn results-cta" onClick={onStudy}>
          Study Weak Areas →
        </button>
        <button className="secondary-btn share-btn" onClick={handleShare}>
          {copied ? '✓ Copied!' : '📤 Share Results'}
        </button>
      </div>
    </div>
  );
}
